/**
 * Round icon button (top-right menu icons, bar-end buttons): silver (or gold) trimmed disc,
 * inline-SVG glyph or image, keybind hint under it, notification badge, optional caption.
 * Activates on the press (dom.ts onPress: pointerdown, `is-pressed` the same frame, no transition).
 */
import { el, onPress } from '../dom';
import { type HudGlyph, glyphSvg, isHudGlyph, svgEl } from './glyphs';
import { intText } from './tokens';

export interface RoundButtonOptions {
  /** A glyph name (kit/glyphs.ts HUD_GLYPHS), raw `<svg …>` markup, an image URL, or a node. */
  icon: HudGlyph | string | Node;
  /** Keybind hint shown under the button ("I", "Esc"). */
  keybind?: string;
  /** Badge count / text (null, '', 0 hide it). `true` shows a dot. */
  badge?: string | number | boolean | null;
  /** Small caption under the button (e.g. "34/90"). Replaces the key hint position. */
  caption?: string;
  size?: 'sm' | 'md' | 'lg';
  gold?: boolean;
  /** Accessible label + native title. */
  label?: string;
  className?: string;
  onPress?: (ev: MouseEvent) => void;
}

export interface RoundButton {
  readonly root: HTMLButtonElement;
  setIcon(icon: RoundButtonOptions['icon']): void;
  setBadge(v: RoundButtonOptions['badge']): void;
  setKeybind(text: string): void;
  setCaption(text: string): void;
  /** Lit state (menu open). */
  setActive(on: boolean): void;
}

function iconNode(icon: RoundButtonOptions['icon']): Node {
  if (typeof icon !== 'string') return icon;
  if (isHudGlyph(icon)) return glyphSvg(icon);
  if (icon.trimStart().startsWith('<svg')) return svgEl(icon);
  const img = document.createElement('img');
  img.src = icon;
  img.alt = '';
  img.draggable = false;
  return img;
}

export function roundButton(parent: HTMLElement | null, opts: RoundButtonOptions): RoundButton {
  const root = el('button', `hk-round hk-hit ${opts.size ?? 'md'}${opts.gold ? ' gold' : ''}${opts.className ? ` ${opts.className}` : ''}`);
  root.type = 'button';
  root.tabIndex = -1;
  if (opts.label) {
    root.setAttribute('aria-label', opts.label);
    root.title = opts.keybind ? `${opts.label} (${opts.keybind})` : opts.label;
  }
  let icon: Node = iconNode(opts.icon);
  root.appendChild(icon);
  const key = el('div', 'hk-key', root, opts.keybind ?? '');
  const caption = el('div', 'hk-round-caption hk-num', root, opts.caption ?? '');
  const badge = el('div', 'hk-badge', root);
  let badgeShown: RoundButtonOptions['badge'] = null;
  let active = false;

  const api: RoundButton = {
    root,
    setIcon(next) {
      const n = iconNode(next);
      root.replaceChild(n, icon);
      icon = n;
    },
    setBadge(v) {
      if (v === badgeShown) return;
      badgeShown = v;
      const dot = v === true;
      badge.classList.toggle('dot', dot);
      badge.textContent = dot || v === false || v == null || v === 0 ? '' : typeof v === 'number' ? (v > 99 ? '99+' : intText(v)) : v;
      badge.classList.toggle('hk-hidden', !dot && !badge.textContent);
    },
    setKeybind(text) {
      if (key.textContent !== text) key.textContent = text;
    },
    setCaption(text) {
      if (caption.textContent !== text) caption.textContent = text;
      root.classList.toggle('has-caption', !!text);
    },
    setActive(on) {
      if (on === active) return;
      active = on;
      root.classList.toggle('active', on);
      root.setAttribute('aria-pressed', String(on));
    },
  };
  api.setBadge(opts.badge ?? null);
  api.setCaption(opts.caption ?? '');
  if (opts.onPress) onPress(root, opts.onPress);
  parent?.appendChild(root);
  return api;
}
